import { useEffect, useRef, useState } from 'react'
import type { TimelineElement } from '../../context/TimelineContext'
import type { Workspace, WorkspaceRole } from '../../types/workspace'
import type { EduSessionSeries, EduSession, EduYearHoliday } from '../../types/schema'
import { isModuleEnabled } from '../../utils/workspaceModules'
import { expandOccurrences, sessionKey, type EduHoliday } from '../../utils/eduSessionPattern'
import {
  listSeriesForTeacher,
  listSeriesForWorkspace,
  listSubstituteSessions,
  listSessionDocsForSeries,
  getYearHolidays,
} from '../../services/eduTimetableService'
import { buildEventsFromEduSessions, type CalendarEvent } from './calendarHelpers'
import type { TimetableOccurrence } from '../Education/timetableHelpers'

export interface UseEduCalendarSessionsOptions {
  workspaceId?: string | null
  uid?: string | null
  windowStart: string
  windowEnd: string
  mode: 'MINE' | 'ALL' | 'OFF'
  isRestricted?: boolean
  role?: WorkspaceRole
  workspace?: Pick<Workspace, 'modules'> | null
  elements?: TimelineElement[]
  directoryMembers?: Array<{ uid: string; displayName?: string; name?: string }>
}

export function useEduCalendarSessions({
  workspaceId,
  uid,
  windowStart,
  windowEnd,
  mode,
  isRestricted,
  role,
  workspace,
  elements,
  directoryMembers,
}: UseEduCalendarSessionsOptions): CalendarEvent[] {
  const [events, setEvents] = useState<CalendarEvent[]>([])

  const elementsRef = useRef(elements)
  const directoryMembersRef = useRef(directoryMembers)
  useEffect(() => {
    elementsRef.current = elements
    directoryMembersRef.current = directoryMembers
  }, [elements, directoryMembers])

  const isTeaching = isModuleEnabled(workspace as Workspace | null | undefined, 'teaching')
  const isTraining = isModuleEnabled(workspace as Workspace | null | undefined, 'training')
  const isEduEnabled = isTeaching || isTraining

  useEffect(() => {
    let isCancelled = false

    const isUnrestrictedEditorPlus =
      !isRestricted && (role === 'EDITOR' || role === 'ADMIN' || role === 'OWNER')
    const effectiveMode = mode === 'ALL' && isUnrestrictedEditorPlus ? 'ALL' : 'MINE'

    const fetchSessions = async () => {
      if (!isEduEnabled || mode === 'OFF' || !workspaceId || !uid) {
        if (!isCancelled) {
          setEvents([])
        }
        return
      }
      try {
        let seriesList: EduSessionSeries[] = []
        let subSessions: EduSession[] = []

        if (effectiveMode === 'ALL') {
          seriesList = await listSeriesForWorkspace(workspaceId)
        } else {
          const [tSeries, sSessions] = await Promise.all([
            listSeriesForTeacher(workspaceId, uid),
            listSubstituteSessions(workspaceId, uid, windowStart, windowEnd),
          ])
          seriesList = tSeries
          subSessions = sSessions
        }

        if (isCancelled) return

        // Collect holidays for every root represented
        const rootIds = Array.from(
          new Set([
            ...seriesList.map((s) => s.rootId).filter(Boolean),
            ...subSessions.map((s) => s.rootId).filter(Boolean),
          ]),
        )

        const holidaysByRoot = new Map<string, EduYearHoliday[]>()
        await Promise.all(
          rootIds.map(async (rId) => {
            const h = await getYearHolidays(rId)
            holidaysByRoot.set(rId, h)
          }),
        )

        if (isCancelled) return

        // Query session docs for series in window
        const seriesIds = seriesList.map((s) => s.id).filter(Boolean) as string[]
        let savedDocs: EduSession[] = []
        if (seriesIds.length > 0) {
          savedDocs = await listSessionDocsForSeries(workspaceId, seriesIds, windowStart, windowEnd)
        }

        if (isCancelled) return

        const savedDocsMap = new Map<string, EduSession>()
        for (const doc of savedDocs) {
          const key = doc.seriesId ? sessionKey(doc.seriesId, doc.date) : doc.id
          if (key) savedDocsMap.set(key, doc)
        }
        for (const doc of subSessions) {
          const key = doc.seriesId ? sessionKey(doc.seriesId, doc.date) : doc.id
          if (key && !savedDocsMap.has(key)) savedDocsMap.set(key, doc)
        }

        // Expand occurrences for each series
        const occurrences: TimetableOccurrence[] = []
        const expandedKeys = new Set<string>()

        for (const series of seriesList) {
          if (!series.id) continue
          const rawHolidays = holidaysByRoot.get(series.rootId) || []
          const eduHolidays: EduHoliday[] = rawHolidays.map((h) => ({
            from: h.from,
            to: h.to,
            label: h.label,
          }))

          const dates = expandOccurrences(
            {
              weekdays: series.weekdays,
              startTime: series.startTime,
              durationMin: series.durationMin,
              fromDate: series.fromDate,
              toDate: series.toDate,
            },
            eduHolidays,
            windowStart,
            windowEnd,
          )

          for (const date of dates) {
            const key = sessionKey(series.id, date)
            expandedKeys.add(key)
            const saved = savedDocsMap.get(key)
            occurrences.push({
              id: key,
              seriesId: series.id,
              date,
              startTime: series.startTime,
              durationMin: series.durationMin,
              nodeId: series.nodeId,
              classId: series.classId,
              teacherUids: series.teacherUids || [],
              roomId: series.roomId,
              onlineUrl: series.onlineUrl,
              status: saved?.status ?? 'PLANNED',
              substituteUid: saved?.substituteUid ?? null,
              topicItemIds: saved?.topicItemIds ?? [],
              note: saved?.note ?? null,
            })
          }
        }

        // Add substitute sessions not already expanded
        for (const sub of subSessions) {
          const key = sub.seriesId ? sessionKey(sub.seriesId, sub.date) : sub.id
          if (key && !expandedKeys.has(key)) {
            const rootHolidays = holidaysByRoot.get(sub.rootId) || []
            const onHoliday = rootHolidays.some((h) => h.from <= sub.date && sub.date <= h.to)
            if (!onHoliday) {
              occurrences.push({
                id: key,
                seriesId: sub.seriesId,
                date: sub.date,
                startTime: sub.startTime || '09:00',
                durationMin: sub.durationMin || 60,
                nodeId: sub.nodeId,
                classId: sub.classId,
                teacherUids: sub.teacherUids || [],
                roomId: null,
                onlineUrl: null,
                status: sub.status ?? 'PLANNED',
                substituteUid: sub.substituteUid ?? uid,
                topicItemIds: sub.topicItemIds ?? [],
                note: sub.note ?? null,
              })
            }
          }
        }

        const evts = buildEventsFromEduSessions(occurrences, {
          elements: elementsRef.current,
          directoryMembers: directoryMembersRef.current,
        })
        if (!isCancelled) {
          setEvents(evts)
        }
      } catch (err) {
        console.warn('[useEduCalendarSessions] failed to load sessions', err)
        if (!isCancelled) {
          setEvents([])
        }
      }
    }

    void fetchSessions()

    return () => {
      isCancelled = true
    }
  }, [
    workspaceId,
    uid,
    windowStart,
    windowEnd,
    mode,
    isRestricted,
    role,
    isEduEnabled,
  ])

  return events
}
